/**
 * Username step: pick a handle + check availability. Presentational — the parent owns the
 * check/availability state. See specs/onboarding-questionnaire/spec.md (Story 2).
 */

import { View, Text, TextInput, Pressable } from 'react-native';
import { AnimatedMonkey } from '../../AnimatedMonkey/AnimatedMonkey';
import { onboardingStyles as s, C } from '../OnboardingFlow.styles';
import { ONBOARDING_TEST_IDS, ONBOARDING_COPY } from '../OnboardingFlow.constants';
import { BackButton } from './BackButton';

interface UsernameStepProps {
  value: string;
  onChangeText: (t: string) => void;
  onCheck: () => void;
  onContinue: () => void;
  onBack: () => void;
  checkLabel: string;
  checkDisabled: boolean;
  checkAvailable: boolean;
  status: { text: string; color: string } | null;
  continueEnabled: boolean;
  progress: number;
}

export function UsernameStep({
  value, onChangeText, onCheck, onContinue, onBack,
  checkLabel, checkDisabled, checkAvailable, status, continueEnabled, progress,
}: UsernameStepProps) {
  return (
    <>
      <View style={s.topRow}>
        <BackButton onPress={onBack} />
        <View style={s.progressTrack}>
          <View style={[s.progressFill, { width: `${Math.round(progress * 100)}%` }]} />
        </View>
      </View>

      <View style={s.questionHeader}>
        <AnimatedMonkey size={72} />
        <View style={s.smallBubble}>
          <Text style={s.smallBubbleText}>{ONBOARDING_COPY.namePrompt}</Text>
        </View>
      </View>

      <View style={s.content}>
        <Text style={s.heading}>{ONBOARDING_COPY.nameHeading}</Text>

        <View style={s.inputRow}>
          <TextInput
            value={value}
            onChangeText={onChangeText}
            placeholder={ONBOARDING_COPY.namePlaceholder}
            placeholderTextColor={C.muted}
            autoCapitalize="none"
            autoCorrect={false}
            maxLength={20}
            testID={ONBOARDING_TEST_IDS.USERNAME_INPUT}
            style={s.input}
            onSubmitEditing={onCheck}
            returnKeyType="done"
          />
          <Pressable
            onPress={onCheck}
            disabled={checkDisabled}
            testID={ONBOARDING_TEST_IDS.CHECK_BTN}
            accessibilityRole="button"
            accessibilityLabel="Check username availability"
            style={({ pressed }) => [
              s.checkButton,
              { backgroundColor: checkDisabled ? C.border : checkAvailable ? C.good : C.accent },
              pressed && s.pressed,
            ]}
          >
            <Text style={[s.checkButtonText, { color: checkDisabled ? C.muted : C.accentInk }]}>
              {checkLabel}
            </Text>
          </Pressable>
        </View>

        {status ? (
          <Text style={[s.statusText, { color: status.color }]}>{status.text}</Text>
        ) : (
          <Text style={s.hintText}>{ONBOARDING_COPY.nameHint}</Text>
        )}
      </View>

      <View style={s.footer}>
        <Pressable
          onPress={onContinue}
          disabled={!continueEnabled}
          testID={ONBOARDING_TEST_IDS.CONTINUE_BTN}
          accessibilityRole="button"
          accessibilityLabel={ONBOARDING_COPY.continue}
          style={({ pressed }) => [
            s.primaryButton,
            !continueEnabled && s.primaryButtonDisabled,
            pressed && continueEnabled && s.pressed,
          ]}
        >
          <Text style={s.primaryButtonText}>{ONBOARDING_COPY.continue}</Text>
        </Pressable>
      </View>
    </>
  );
}
